import React, { useState } from "react";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaPlay,
  FaRocket,
  FaRobot,
  FaArrowLeft,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
} from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import "../../styles/kids-program.css";

const RoboticsForKids = () => {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const modules = [
    {
      id: 1,
      title: "Introduction to Robotics",
      icon: "🤖",
      topics: [
        "What is Robotics?",
        "Types of robots and their uses",
        "Introduction to LEGO Mindstorms",
        "Understanding robot components (motors, sensors, controllers)",
        "Building your first simple robot",
      ],
    },
    {
      id: 2,
      title: "Robot Building Basics",
      icon: "🔧",
      topics: [
        "Understanding robot chassis and structure",
        "Adding motors and wheels",
        "Installing sensors and controllers",
        "Wiring and connections",
        "Testing your robot build",
      ],
    },
    {
      id: 3,
      title: "Programming Your Robot",
      icon: "💻",
      topics: [
        "Introduction to block-based robot programming",
        "Making your robot move forward/backward",
        "Programming turns and rotations",
        "Using loops for repeated actions",
        "Creating robot behaviors",
      ],
    },
    {
      id: 4,
      title: "Working with Sensors",
      icon: "📡",
      topics: [
        "Ultrasonic sensors for distance detection",
        "Color sensors for line following",
        "Touch sensors for obstacle detection",
        "Gyro sensors for balance",
        "Combining multiple sensors",
      ],
    },
    {
      id: 5,
      title: "Advanced Robot Programming",
      icon: "⚙️",
      topics: [
        "Creating autonomous robots",
        "Line following robot programming",
        "Obstacle avoidance algorithms",
        "Remote control via Bluetooth",
        "Robot competitions preparation",
      ],
    },
    {
      id: 6,
      title: "Final Robot Challenge",
      icon: "🏆",
      topics: [
        "Design your own robot",
        "Complete robot assembly and programming",
        "Testing and debugging",
        "Robot competition day",
        "Certificate and showcase",
      ],
    },
  ];

  const projects = [
    { name: "Line Follower", icon: "📏", description: "Robot that follows black lines on white surface" },
    { name: "Obstacle Avenger", icon: "🚧", description: "Robot that avoids obstacles automatically" },
    { name: "Remote Control Bot", icon: "📱", description: "Control your robot with a smartphone" },
    { name: "Sumo Robot", icon: "🏆", description: "Push other robots out of the ring" },
  ];

  const faqs = [
    {
      q: "Do I need to buy a robot kit?",
      a: "Yes, you'll need a LEGO Mindstorms or compatible robot kit. We can provide kits at additional cost.",
    },
    {
      q: "What age group is this suitable for?",
      a: "This course is designed for kids aged 10-16 who love building and programming.",
    },
    {
      q: "Do I need prior programming experience?",
      a: "No prior programming experience is required. We use block-based programming for beginners.",
    },
    {
      q: "Will my child get a certificate?",
      a: "Yes, every student receives a completion certificate after finishing the course.",
    },
    {
      q: "Can my child participate in robot competitions?",
      a: "Yes! We prepare students for various robot competition events.",
    },
  ];

  const toggleModule = (id) => {
    setActiveModule(activeModule === id ? null : id);
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleStartLearning = () => {
    document.getElementById("curriculum")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleWatchDemo = () => setShowDemoModal(true);
  const handleEnrollNow = () => navigate("/register");
  const handleBookDemo = () => navigate("/contact");
  const handleBackToKids = () => navigate("/kids");

  return (
    <div className="kids-program-page">
      <PublicNavbar />

      <section className="kids-program-hero kids-program-hero-purple">
        <div className="kids-program-hero-bubbles">
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
          <div className="kids-program-bubble"></div>
        </div>
        
        <Container>
          <Button variant="link" className="kids-program-back-btn" onClick={handleBackToKids}>
            <FaArrowLeft className="me-2" /> Back to Kids Programs
          </Button>

          <Row className="align-items-center">
            <Col lg={7}>
              <div className="kids-program-hero-content">
                <div className="kids-program-hero-badge">
                  <span>🤖</span><span>Build & Program Real Robots!</span><span>⚡</span>
                </div>
                <h1 className="kids-program-hero-title">
                  Become a <span>Robotics Engineer</span> & Build Smart Robots!
                </h1>
                <p className="kids-program-hero-description">
                  Design, build, and program real robots using LEGO Mindstorms. Learn engineering concepts while having fun creating robots that move, sense, and think!
                </p>
                <div className="kids-program-hero-buttons">
                  <Button className="kids-program-btn-primary" onClick={handleStartLearning}>
                    <FaRocket className="me-2" /> Start Learning
                  </Button>
                  <Button className="kids-program-btn-outline" onClick={handleWatchDemo}>
                    <FaPlay className="me-2" /> Watch Demo
                  </Button>
                </div>
                <div className="kids-program-hero-stats">
                  <div className="kids-program-stat"><span className="kids-program-stat-number">10+</span><span className="kids-program-stat-label">Robots Built</span></div>
                  <div className="kids-program-stat"><span className="kids-program-stat-number">8+</span><span className="kids-program-stat-label">Sensor Types</span></div>
                  <div className="kids-program-stat"><span className="kids-program-stat-number">100%</span><span className="kids-program-stat-label">Hands-on</span></div>
                </div>
              </div>
            </Col>
            <Col lg={5}>
              <div className="kids-program-hero-illustration">
                <div className="kids-program-floating-icon kids-program-icon-1">🤖</div>
                <div className="kids-program-floating-icon kids-program-icon-2">🔧</div>
                <div className="kids-program-floating-icon kids-program-icon-3">⚙️</div>
                <div className="kids-program-floating-icon kids-program-icon-4">📡</div>
                <div className="kids-program-floating-icon kids-program-icon-5">🏆</div>
                <div className="kids-program-floating-icon kids-program-icon-6">💡</div>
                <div className="kids-program-main-icon"><FaRobot /></div>
              </div>
            </Col>
          </Row>
        </Container>

        <div className="kids-program-hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      <section className="kids-program-learn">
        <Container>
          <div className="kids-program-section-header"><h2>What You'll Learn 🎯</h2><p>By the end of this course, you'll be able to</p></div>
          <Row className="g-4">
            {[
              { icon: "🔧", title: "Robot Building", desc: "Design and assemble real robots from scratch" },
              { icon: "💻", title: "Robot Programming", desc: "Code robots to perform complex tasks" },
              { icon: "📡", title: "Sensor Integration", desc: "Use sensors for intelligent robot behavior" },
              { icon: "🏆", title: "Robot Competitions", desc: "Prepare for robotics competitions" }
            ].map((item, i) => (
              <Col md={6} lg={3} key={i}>
                <div className="kids-program-learn-card">
                  <div className="kids-program-learn-icon kids-program-learn-icon-purple">{item.icon}</div>
                  <h4>{item.title}</h4><p>{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section id="curriculum" className="kids-program-curriculum">
        <Container>
          <div className="kids-program-section-header"><h2>📚 Course Curriculum</h2><p>6 exciting modules to become a robotics engineer</p></div>
          <div className="kids-program-modules">
            {modules.map((module) => (
              <div key={module.id} className={`kids-program-module kids-program-module-purple ${activeModule === module.id ? "active" : ""}`}>
                <div className="kids-program-module-header" onClick={() => toggleModule(module.id)}>
                  <h3><span className="kids-program-module-icon">{module.icon}</span>Module {module.id}: {module.title}</h3>
                  <span className="kids-program-module-toggle">{activeModule === module.id ? <FaChevronUp /> : <FaChevronDown />}</span>
                </div>
                <div className="kids-program-module-content">
                  <ul>{module.topics.map((topic, idx) => (<li key={idx}><FaCheckCircle className="me-2" style={{ color: "#9C27B0" }} />{topic}</li>))}</ul>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="kids-program-projects">
        <Container>
          <div className="kids-program-section-header"><h2>🤖 Robots You'll Build</h2><p>Create these amazing robots and more!</p></div>
          <Row className="g-4">{projects.map((p, i) => (<Col md={6} lg={3} key={i}><div className="kids-program-project-card"><div className="kids-program-project-icon">{p.icon}</div><h4>{p.name}</h4><p>{p.description}</p></div></Col>))}</Row>
        </Container>
      </section>

      <section className="kids-program-why kids-program-why-purple">
        <Container>
          <div className="kids-program-section-header text-white"><h2 className="text-white">Why Choose This Course? 🌟</h2><p className="text-white">The perfect start for future robotics engineers</p></div>
          <div className="kids-program-why-grid">
            {[["🤖", "Real Robots", "Build actual working robots"], ["🔧", "Hands-on Learning", "Learn by building and programming"], ["🏆", "Certificate", "Get certified on completion"], ["⚙️", "Competition Ready", "Prepare for robot competitions"]].map((item, i) => (
              <div key={i} className="kids-program-why-item"><div className="kids-program-why-icon">{item[0]}</div><h4>{item[1]}</h4><p>{item[2]}</p></div>
            ))}
          </div>
        </Container>
      </section>

      <section className="kids-program-faq">
        <Container>
          <div className="kids-program-section-header"><h2>❓ Frequently Asked Questions</h2><p>Everything parents need to know</p></div>
          <div className="kids-program-faq-container">{faqs.map((faq, idx) => (<div key={idx} className={`kids-program-faq-item ${openFaqIndex === idx ? "active" : ""}`}><div className="kids-program-faq-question" onClick={() => toggleFaq(idx)}><span>{faq.q}</span><span>{openFaqIndex === idx ? <FaChevronUp /> : <FaChevronDown />}</span></div><div className="kids-program-faq-answer"><p>{faq.a}</p></div></div>))}</div>
        </Container>
      </section>

      <section className="kids-program-cta">
        <Container><h2>🤖 Ready to Build Your First Robot?</h2><p>Join young engineers who are already building amazing robots!</p><div className="kids-program-cta-buttons"><Button className="kids-program-cta-primary" onClick={handleEnrollNow}>Enroll Now ✨</Button><Button className="kids-program-cta-secondary" onClick={handleBookDemo}>Book Free Demo 📅</Button></div></Container>
      </section>

      <Modal show={showDemoModal} onHide={() => setShowDemoModal(false)} size="lg" centered className="kids-program-modal">
        <Modal.Header closeButton><Modal.Title><FaPlay className="me-2" /> Robotics Demo Class</Modal.Title></Modal.Header>
        <Modal.Body className="p-0"><div className="video-wrapper"><iframe src="https://www.youtube.com/embed/QnubPziozsw" title="Demo" frameBorder="0" allowFullScreen></iframe></div></Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowDemoModal(false)}>Close</Button></Modal.Footer>
      </Modal>
      <Footer />
    </div>
  );
};

export default RoboticsForKids;